import logo from "../../assets/image/logo.svg";
import { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ConfigContext from "../../context/config/ConfigContext";
import { useLocation, useNavigate } from "react-router-dom";
import Divider from "../../components/divider/Divider";
import Card from "../../components/card/Card";
import Category from "../../components/icons/Category";
import { ArrowUp, Info } from "lucide-react";
import SkeletonCategorie from "../../components/skeleton/SkeletonCategorie";
import SkeletonProdotti from "../../components/skeleton/SkeletonProdotti";
import ScrollableCategoryBar from "../../components/scrollable/ScrollableCategoryBar";

function Menu() {
	const navigate = useNavigate();
	const {
		categories,
		selectedCategory,
		setSelectedCategory,
		products,
		strings,
		isLoadingProducts
	} = useContext(ConfigContext);

	const location = useLocation();
	const { preselectedCategoryId } = location.state || {};

	const selectedCategoryRef = useRef(null);
	const categoryScrollRef = useRef(null); // <-- ref vero sul div scrollabile
	const triggerRef = useRef(null);

	const [showScrollTop, setShowScrollTop] = useState(false);
	const [isLoading, setIsLoading] = useState(!categories?.length);
	const [isSticky, setIsSticky] = useState(false);

	/*
	useEffect(() => {
		// Mostra solo se non già visto
		if (!hasSeenModal("oktoberfest")) {
			const timer = setTimeout(() => {
				openModal("oktoberfest");
			}, 2000); // <-- 2 secondi di delay

			// cleanup del timeout se l’utente lascia la pagina prima
			return () => clearTimeout(timer);
		}
	}, [openModal, hasSeenModal]);
	*/

	useEffect(() => {
		//openModal("oktoberfest");
		const handleScroll = () => {
			const scrollY = window.scrollY;

			setShowScrollTop((prev) => {
				if (scrollY > 320 && !prev) return true;
				if (scrollY < 280 && prev) return false;
				return prev;
			});
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		if (categories) setTimeout(() => setIsLoading(false), 1000);
	}, [categories, products]);

	useEffect(() => {
		if (!isLoading && !isLoadingProducts) {
			if (!preselectedCategoryId) {
				const savedScroll = sessionStorage.getItem("scrollPosition");
				const savedCatScroll = sessionStorage.getItem("categoryScroll");

				if (savedScroll) window.scrollTo(0, parseInt(savedScroll));

				if (savedCatScroll && categoryScrollRef.current) {
					requestAnimationFrame(() => {
						categoryScrollRef.current.scrollLeft = parseInt(savedCatScroll);
					});
				}

				sessionStorage.removeItem("scrollPosition");
				sessionStorage.removeItem("categoryScroll");
			} else {
				window.scrollTo({ top: 0, behavior: "auto" });
			}
		}
	}, [isLoading, isLoadingProducts, preselectedCategoryId]);

	useEffect(() => {
		if (preselectedCategoryId && categories.length > 0) {
			const found = categories.find((cat) => cat.id === preselectedCategoryId);
			if (found) {
				setSelectedCategory(found.id);
			}
		}
	}, [preselectedCategoryId, categories, setSelectedCategory]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => setIsSticky(!entry.isIntersecting),
			{ threshold: 0 }
		);
		if (triggerRef.current) observer.observe(triggerRef.current);
		return () => observer.disconnect();
	}, []);

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const renderCategoryBar = () => (
		<ScrollableCategoryBar innerRef={categoryScrollRef}>
			{categories?.map((item, index) => {
				const isSelected = selectedCategory === item.id;
				const itemRef = isSelected ? selectedCategoryRef : null;

				return (
					<Category
						ref={itemRef}
						key={index}
						name={item.name}
						icon={item.icon}
						isSelected={isSelected}
						onClick={() => {
							if (item.id !== selectedCategory) {
								setSelectedCategory(item.id);
								setTimeout(() => {
									scrollToTop();
									selectedCategoryRef.current?.scrollIntoView({
										behavior: "smooth",
										inline: "center",
										block: "nearest",
									});
								}, 50);
							}
						}}
					/>
				);
			})}
		</ScrollableCategoryBar>
	);

	return (
		<main className="min-h-safe-screen pb-10 bg-background text-secondary">
			{/* Logo */}
			<div className="flex justify-center py-3 bg-surface shadow-sm border-b border-accent">
				<img onClick={() => navigate("/")} src={logo} className="h-16 cursor-pointer" alt="logo" />
			</div>

			{/* Trigger invisibile per sticky */}
			<div ref={triggerRef} className="h-1" />

			{/* Barra categorie con sticky e animazione */}
			<motion.div
				className={`z-30 ${isSticky
					? "fixed top-0 left-0 right-0 border-b bg-background border-accent shadow-md px-4 pt-4 pb-3"
					: "px-4 pt-4 pb-3"
					}`}
				initial={false}
				animate={{ y: 0, opacity: 1 }}
				transition={{ duration: 0.3 }}
			>

				{isLoading ? <SkeletonCategorie /> : renderCategoryBar()}
			</motion.div>

			{isSticky && <div className="h-[84px]" />}

			{/* Prodotti */}
			<AnimatePresence mode="wait">
				{isLoading || isLoadingProducts ? (
					<motion.div
						key="skeleton"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
					>
						<SkeletonProdotti />
					</motion.div>
				) : (
					<motion.div
						key={`products-${selectedCategory}`}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2 }}
					>
						{products?.map((item, index) => (
							<div key={index} className="px-4 py-2">
								{item?.subcategory && <Divider title={item?.subcategory?.title} />}
								<div className="flex flex-col gap-3 max-w-md mx-auto">
									{item?.data?.map((product, idx) => (
										<Card
											key={idx}
											title={product.title}
											image={product.image}
											price={product.price}
											isAvailable={product.available}
											ingredients={product?.ingredients}
											type={product.type}
											alcoholPercentage={product.alcoholPercentage}
											quantity={product.quantity}
											showDetailPage={product.showDetailPage}
											onClick={() => {
												sessionStorage.setItem("scrollPosition", window.scrollY.toString());
												sessionStorage.setItem(
													"categoryScroll",
													categoryScrollRef.current?.scrollLeft?.toString() || "0"
												);
												if (product.id === "ab568c25-7116-4880-90ab-b0d6295d6f6e") {
													navigate("/crea/hamburger")
												} else {
													navigate(`${product.id}`, {
														state: {
															products: item.data,
															currentIndex: idx,
														},
													});
												}
											}}
										/>
									))}
								</div>
							</div>
						))}
					</motion.div>
				)}
			</AnimatePresence>

			{/* Footer Note */}
			<div className="bg-surface px-6 py-6 mt-5 text-sm text-muted max-w-md mx-auto rounded-xl shadow-inner border border-accent space-y-1">
				<div className="flex items-center gap-2 mb-2">
					<Info className="w-4 h-4 text-primary" />
					<p className="uppercase font-semibold text-secondary tracking-wider text-xs">
						{strings?.balconcinoHeader}
					</p>
				</div>
				<p>{strings?.balconcino1}</p>
				<p>{strings?.balconcino2}</p>
				<p>{strings?.balconcino3}</p>
			</div>

			{/* Scroll Top Button */}
			{isSticky && showScrollTop && (
				<button
					onClick={scrollToTop}
					aria-label="Torna su"
					className="fixed bottom-5 right-5 z-50 bg-primary text-black p-2 rounded-full shadow-md hover:scale-110 transition duration-200"
				>
					<ArrowUp className="w-5 h-5" />
				</button>
			)}
		</main>
	);
}

export default Menu;